import { useFormik } from "formik";
import {
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from "recoil";
import {
  atomCurrentStep,
  atomDossier,
  atomEvaluatePremium,
  atomFormikGroup,
  atomFormikInsurance,
  atomIsGroup,
  atomPolicy,
  atomDossierConfig,
  atomQuestionnaire,
  atomShowQuestionnaireFailure,
  defaultValueInsurance,
  selectorAgency,
  selectorFractionations,
} from "src/storage";
import { FormikInsurance } from "src/types";
import {
  useValidationSchemas,
  useValidationSchemasGroup,
} from "./handlers/validation-schemas.hook";
import { useNavigate } from "react-router";
import { Paths } from "src/navigation";
import { useEffect, useMemo } from "react";
import { DEFAULT_AGENCY_CODE } from "src/config";
import { useTranslation } from "react-i18next";
import { useResponsiveBp } from "src/utils/hook-functions/responsive-bp.hook";
import {
  toPolicyRequest,
  toQuestionnaireModified,
  usePostPolicy,
  usePostQuestionnaire,
  usePutPolicy,
} from "src/networking/terminals";

export const useDataEntryWrapper = ({ step }: { step: number }) => {
  const { t } = useTranslation();

  const { isDesktop } = useResponsiveBp();

  const agency = useRecoilValue(selectorAgency);
  const dossier = useRecoilValue(atomDossier);
  const prConfig = useRecoilValue(atomDossierConfig);

  const insuredLimit = Number(prConfig?.configurazioni.limiteAssicurati) || 7;

  // Groups =========================================================
  const [isGroup, setIsGroup] = useRecoilState(atomIsGroup);
  const setGroupInfo = useSetRecoilState(atomFormikGroup);

  // Current Step ===================================================
  const [currentStep, saveCurrentStep] = useRecoilState(atomCurrentStep);

  // Reset ==========================================================
  const resetEvaluatePremium = useResetRecoilState(atomEvaluatePremium);

  // Main Formik ====================================================
  const [FormikInsurance, saveFormikMainInsured] =
    useRecoilState(atomFormikInsurance);
  const normalValidationSchemas = useValidationSchemas();
  const groupValidationSchemas = useValidationSchemasGroup();

  const saveShowQuestionnaireFailure = useSetRecoilState(
    atomShowQuestionnaireFailure
  );

  const insuranceFormik = useFormik<FormikInsurance>({
    initialValues: FormikInsurance,
    validationSchema: !isGroup
      ? normalValidationSchemas[currentStep]
      : groupValidationSchemas[currentStep],
    onSubmit: (values, { setTouched, setSubmitting }) => {
      setTouched({});
      saveFormikMainInsured(values);

      const totalInsured = Number(values?.insuredNumber);

      if (totalInsured > insuredLimit) setIsGroup(true);

      if (isGroup) {
        if (currentStep === 2 || currentStep === 4) {
          managePolicy();
          return;
        }

        //Continue only if the questionnaire is valid
        if (currentStep === 3) {
          validateQuestionnaire();
          return;
        }

        saveCurrentStep((x) => Math.min(6, x + 1));
      } else {
        setSubmitting(false);

        if (currentStep === 1 || currentStep === 3) {
          managePolicy();
          return;
        }

        //Continue only if the questionnaire is valid
        if (currentStep === 2) {
          validateQuestionnaire();
          return;
        }

        saveCurrentStep((x) => Math.min(5, x + 1));
      }
    },
  });
  const { values, setValues } = insuranceFormik;

  // Navigation =====================================================
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNaN(step)) {
      saveCurrentStep(step);
    }
  }, [step]);

  const navigableGroupsStepPathMap: { [key: number]: string } = {
    0: Paths.DataEntry1,
    1: Paths.EntryGroup2,
    2: Paths.EntryGroup3,
    3: Paths.EntryGroup4,
    4: Paths.EntryGroup5,
    5: Paths.EntryGroup6,
  };

  const navigableStepPathMap: { [key: number]: string } = {
    0: Paths.DataEntry1,
    1: Paths.DataEntry2,
    2: Paths.DataEntry3,
    3: Paths.DataEntry4,
    4: Paths.DataEntry5,
  };

  //Manage navigation logic between steps
  useEffect(() => {
    const nextPath = !isGroup
      ? navigableStepPathMap[currentStep]
      : navigableGroupsStepPathMap[currentStep];
    if (!nextPath) return;

    const currPath = window.location.pathname;

    // Avoid double navigation
    if (!currPath.includes(nextPath)) {
      navigate({
        pathname: nextPath,
        search: `?base=${values?.coverageTypeRadios === "1"}`,
      });
    }
  }, [currentStep]);

  const goToLanding = () => {
    const agencyCode =
      agency && agency?.id !== DEFAULT_AGENCY_CODE ? agency?.id : "";
    navigate(Paths.Landing + "?" + agencyCode);
  };

  //Manage goBack logic between steps
  const goBack = () => {
    //Reset Data
    resetEvaluatePremium();

    if (currentStep === 0) {
      goToLanding();
      return;
    }

    setValues({ ...values });
    saveCurrentStep((x) => Math.max(0, x - 1));

    if (isGroup) {
      const newInsuranceValue: FormikInsurance = {
        ...defaultValueInsurance,
        insuredDaysNumber: values.insuredDaysNumber,
        insuredPeriodType: values.insuredPeriodType,
        coverageTypeRadios: values.coverageTypeRadios,
        insuredNumber: values.insuredNumber,
      };
      switch (currentStep) {
        case 1:
          setValues(newInsuranceValue);
          saveFormikMainInsured(newInsuranceValue);
          setIsGroup(false);
          setGroupInfo((prev) => ({
            ...prev,
            group: [],
            filesStatus: undefined,
            fileName: "",
          }));
          break;
        case 2:
          saveFormikMainInsured({ ...values });
          break;
        case 3:
          saveFormikMainInsured({ ...values, coherenceQuestions: [] });
          break;
        default:
          break;
      }
    } else {
      switch (currentStep) {
        case 1:
          saveFormikMainInsured({ ...values });
          break;
        case 2:
          saveFormikMainInsured({ ...values, coherenceQuestions: [] });
          break;
        default:
          break;
      }
    }
  };

  // Policy ===============================================
  const [policy, savePolicy] = useRecoilState(atomPolicy);
  const fractionations = useRecoilValue(
    selectorFractionations(values.coverageTypeRadios)
  );
  const { crossGate: postPolicy, pending: postPolicyPending } = usePostPolicy();
  const { crossGate: putPolicy, pending: putPolicyPending } = usePutPolicy();
  const managePolicy = () => {
    const dossierId = dossier?.dossierId;
    const agencyId = agency?.id;
    const body = toPolicyRequest(values, fractionations ?? []);
    if (!body || !agencyId || !dossierId) return;

    if (!policy)
      postPolicy(
        {
          headers: {
            xDossierId: dossierId,
            AgenziaId: agencyId,
          },
          body: body,
        },
        {
          onSuccess: (policy) => {
            savePolicy(policy);
            saveCurrentStep((x) => Math.min(6, x + 1));
          },
        }
      );
    else
      putPolicy(
        {
          headers: {
            xDossierId: dossier.dossierId,
          },
          uriParams: {
            idPolizza: policy.idPolizza || "",
          },
          body: body,
        },
        {
          onSuccess: (policy) => {
            savePolicy(policy);
            saveCurrentStep((x) => Math.min(6, x + 1));
          },
        }
      );
  };

  // Questionnaire ===============================================
  const questionnaire = useRecoilValue(atomQuestionnaire);
  const { crossGate: postQuestionnaire, pending: postQuestionnairePending } =
    usePostQuestionnaire();

  const validateQuestionnaire = () => {
    if (!dossier || !policy || !questionnaire) return;

    const body = toQuestionnaireModified(questionnaire, values);

    postQuestionnaire(
      {
        headers: {
          xDossierId: dossier?.dossierId,
        },
        uriParams: {
          idPolizza: policy?.idPolizza || "",
        },
        body: body,
      },
      {
        onSuccess(questionnaire) {
          const questionnairResult = questionnaire.questionario.isOk;
          saveShowQuestionnaireFailure(!questionnairResult);

          if (questionnairResult) saveCurrentStep((x) => Math.min(6, x + 1));
        },
      }
    );
  };

  const continueDisabled = useMemo(() => {
    if (currentStep === 2) {
      return values.coherenceQuestions.some((question) => !question.answer);
    }

    if (currentStep === 3) {
      return !policy?.premioTotale;
    }

    return false;
  }, [currentStep, values.coherenceQuestions, policy?.premioTotale]);

  const resumePurchaseAccordionIsVisible = useMemo(
    () =>
      !isDesktop &&
      (!isGroup
        ? currentStep === 2 || currentStep === 3
        : currentStep === 3 || currentStep === 4),
    [isGroup, currentStep, isDesktop]
  );

  return {
    isDesktop,
    currentStep,
    isGroup,
    insuranceFormik,
    normalValidationSchemas,
    agency,
    continueDisabled,
    postPolicyPending,
    putPolicyPending,
    postQuestionnairePending,
    resumePurchaseAccordionIsVisible,
    t,
    goBack,
  };
};
